import { formatCurrency } from '@/utils/formatters';
import { isValidCnpj } from '@/utils/validators';
import type { RawEntityType } from '@/types/entity-data';

// Every date a process carries is a calendar day, never a timestamp.
export const LAWSUIT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

// Predictus gives one tax id per counterparty without saying which kind it is, so
// the shape of the number is the only signal: 14 valid digits means a CNPJ.
export function counterpartyType(taxId: string | null): RawEntityType {
  return taxId && isValidCnpj(taxId) ? 'company' : 'person';
}

// Brazilian CNJ process numbers are 20 digits: NNNNNNN-DD.AAAA.J.TR.OOOO.
export function formatProcessNumber(value: string | null): string {
  if (!value) return 'Sem número';
  const digits = value.replace(/\D/g, '');
  if (digits.length === 20) {
    return `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16, 20)}`;
  }
  return value;
}

// Polo ativo -> swords (orange, the attacking side); polo passivo -> shield (blue).
export function positionMeta(side: string | null): { icon: string; color: string } {
  const s = (side || '').toLowerCase();
  if (s.includes('passiv')) return { icon: 'security', color: 'text-blue-600' };
  if (s.includes('ativ')) return { icon: 'swords', color: 'text-orange-500' };
  return { icon: 'help', color: 'text-surface-400' };
}

export function formatMoney(value: number | null, currency: string | null): string {
  if (value === null || value === undefined) return '—';
  const cur = (currency || 'BRL').toUpperCase();
  if (cur === 'BRL') return formatCurrency(value, 'BRL');
  const num = Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${num} ${currency}`;
}

// Abbreviated form for aggregate totals (stat tiles) where a full precision value
// would overflow a small box — "mil"/"Mi"/"Bi" instead of wrapping/truncating the
// full number. Per-row amounts should keep using formatMoney for exact precision.
export function formatMoneyCompact(value: number | null, currency: string | null): string {
  if (value === null || value === undefined) return '—';
  const cur = (currency || 'BRL').toUpperCase();
  const prefix = cur === 'BRL' ? 'R$' : cur;
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000) {
    return `${prefix} ${(value / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} Bi`;
  }
  if (abs >= 1_000_000) {
    return `${prefix} ${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} Mi`;
  }
  if (abs >= 1_000) {
    return `${prefix} ${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  }
  return formatMoney(value, currency);
}
